import { useEffect } from 'react';
import AOS from 'aos';
import Typed from 'typed.js';
import PureCounter from '@srexi/purecounterjs';
import GLightbox from 'glightbox';
import imagesLoaded from 'imagesloaded';
import Isotope from 'isotope-layout';
import Swiper from 'swiper';
import { Autoplay, Pagination } from 'swiper/modules';

const TESTIMONIALS_SWIPER_CONFIG = {
  modules: [Autoplay, Pagination],
  loop: true,
  speed: 600,
  autoplay: { delay: 5000 },
  slidesPerView: 'auto',
  pagination: {
    el: '.swiper-pagination',
    type: 'bullets',
    clickable: true,
  },
  breakpoints: {
    320: { slidesPerView: 1, spaceBetween: 40 },
    1200: { slidesPerView: 3, spaceBetween: 1 },
  },
};

export function useIPortfolio() {
  useEffect(() => {
    document.body.classList.add('index-page');
    return () => document.body.classList.remove('index-page');
  }, []);

  useEffect(() => {
    const cleanups = [];

    function aosInit() {
      AOS.init({
        duration: 600,
        easing: 'ease-in-out',
        once: true,
        mirror: false,
      });
    }
    aosInit();

    new PureCounter();

    const typedEl = document.querySelector('.typed');
    let typedInstance;
    if (typedEl) {
      const strings = typedEl.getAttribute('data-typed-items').split(',');
      typedInstance = new Typed('.typed', {
        strings,
        loop: true,
        typeSpeed: 100,
        backSpeed: 50,
        backDelay: 2000,
      });
    }

    const glightbox = GLightbox({ selector: '.glightbox' });

    document.querySelectorAll('.skills-animation').forEach((item) => {
      const animateBars = () => {
        item.querySelectorAll('.progress .progress-bar').forEach((el) => {
          el.style.width = `${el.getAttribute('aria-valuenow')}%`;
        });
      };

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            animateBars();
            observer.disconnect();
          }
        },
        { rootMargin: '0px 0px -20% 0px', threshold: 0 }
      );

      observer.observe(item);
      cleanups.push(() => observer.disconnect());
    });

    document.querySelectorAll('.isotope-layout').forEach((isotopeItem) => {
      const layout = isotopeItem.getAttribute('data-layout') ?? 'masonry';
      const filter = isotopeItem.getAttribute('data-default-filter') ?? '*';
      const sort = isotopeItem.getAttribute('data-sort') ?? 'original-order';
      const container = isotopeItem.querySelector('.isotope-container');
      let initIsotope;

      const onImagesLoaded = () => {
        const gutter = parseInt(isotopeItem.getAttribute('data-gutter') ?? '12', 10);
        initIsotope = new Isotope(container, {
          itemSelector: '.isotope-item',
          layoutMode: layout,
          percentPosition: true,
          masonry: {
            columnWidth: '.isotope-item',
            gutter,
          },
          filter,
          sortBy: sort,
        });
        initIsotope.layout();
      };

      imagesLoaded(container, onImagesLoaded);

      isotopeItem.querySelectorAll('.isotope-filters li').forEach((filterBtn) => {
        const handler = function onFilterClick() {
          isotopeItem.querySelector('.isotope-filters .filter-active')?.classList.remove('filter-active');
          this.classList.add('filter-active');
          initIsotope?.arrange({ filter: this.getAttribute('data-filter') });
          aosInit();
        };
        filterBtn.addEventListener('click', handler, false);
        cleanups.push(() => filterBtn.removeEventListener('click', handler, false));
      });

      cleanups.push(() => initIsotope?.destroy());
    });

    const swiperEl = document.querySelector('.init-swiper');
    let swiperInstance;
    if (swiperEl) {
      swiperInstance = new Swiper(swiperEl, TESTIMONIALS_SWIPER_CONFIG);
    }

    const headerToggleBtn = document.querySelector('.header-toggle');
    const header = document.querySelector('#header');

    function headerToggle() {
      header?.classList.toggle('header-show');
      headerToggleBtn?.classList.toggle('bi-list');
      headerToggleBtn?.classList.toggle('bi-x');
    }

    if (headerToggleBtn) {
      headerToggleBtn.addEventListener('click', headerToggle);
      cleanups.push(() => headerToggleBtn.removeEventListener('click', headerToggle));
    }

    document.querySelectorAll('#navmenu a').forEach((navLink) => {
      const handler = () => {
        if (document.querySelector('.header-show')) {
          headerToggle();
        }
      };
      navLink.addEventListener('click', handler);
      cleanups.push(() => navLink.removeEventListener('click', handler));
    });

    document.querySelectorAll('.navmenu .toggle-dropdown').forEach((toggle) => {
      const handler = function onDropdownToggle(e) {
        e.preventDefault();
        this.parentNode.classList.toggle('active');
        this.parentNode.nextElementSibling?.classList.toggle('dropdown-active');
        e.stopImmediatePropagation();
      };
      toggle.addEventListener('click', handler);
      cleanups.push(() => toggle.removeEventListener('click', handler));
    });

    const preloader = document.querySelector('#preloader');
    const hidePreloader = () => {
      if (preloader) {
        preloader.classList.add('preloader-done');
        window.setTimeout(() => {
          preloader.style.display = 'none';
        }, 400);
      }
    };
    if (preloader) {
      const timeouts = [];
      const isLanding = preloader.classList.contains('preloader--landing');

      if (isLanding) {
        timeouts.push(window.setTimeout(() => {
          preloader.classList.add('preloader-darkening');
        }, 1800));
        timeouts.push(window.setTimeout(hidePreloader, 2500));
      } else {
        timeouts.push(window.setTimeout(hidePreloader, 2000));
      }

      cleanups.push(() => timeouts.forEach((id) => window.clearTimeout(id)));
    }

    const scrollTop = document.querySelector('.scroll-top');

    function toggleScrollTop() {
      if (scrollTop) {
        if (window.scrollY > 100) {
          scrollTop.classList.add('active');
        } else {
          scrollTop.classList.remove('active');
        }
      }
    }

    const onScrollTopClick = (e) => {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    scrollTop?.addEventListener('click', onScrollTopClick);
    window.addEventListener('load', toggleScrollTop);
    document.addEventListener('scroll', toggleScrollTop);
    cleanups.push(() => {
      scrollTop?.removeEventListener('click', onScrollTopClick);
      window.removeEventListener('load', toggleScrollTop);
      document.removeEventListener('scroll', toggleScrollTop);
    });
    toggleScrollTop();

    const navmenulinks = document.querySelectorAll('.navmenu a');

    function navmenuScrollspy() {
      navmenulinks.forEach((navmenulink) => {
        if (!navmenulink.hash) return;
        const section = document.querySelector(navmenulink.hash);
        if (!section) return;
        const position = window.scrollY + 200;
        if (position >= section.offsetTop && position <= section.offsetTop + section.offsetHeight) {
          document.querySelectorAll('.navmenu a.active').forEach((link) => link.classList.remove('active'));
          navmenulink.classList.add('active');
        } else {
          navmenulink.classList.remove('active');
        }
      });
    }

    window.addEventListener('load', navmenuScrollspy);
    document.addEventListener('scroll', navmenuScrollspy);
    cleanups.push(() => {
      window.removeEventListener('load', navmenuScrollspy);
      document.removeEventListener('scroll', navmenuScrollspy);
    });
    navmenuScrollspy();

    const onHashLoad = () => {
      if (window.location.hash) {
        const section = document.querySelector(window.location.hash);
        if (section) {
          setTimeout(() => {
            const scrollMarginTop = getComputedStyle(section).scrollMarginTop;
            window.scrollTo({
              top: section.offsetTop - parseInt(scrollMarginTop, 10),
              behavior: 'smooth',
            });
          }, 100);
        }
      }
    };
    window.addEventListener('load', onHashLoad);
    cleanups.push(() => window.removeEventListener('load', onHashLoad));

    return () => {
      typedInstance?.destroy();
      glightbox?.destroy();
      swiperInstance?.destroy();
      cleanups.forEach((fn) => fn());
    };
  }, []);
}
